import { createSlice } from "@reduxjs/toolkit";
import { postVendorsInvoices } from "./thunk";

// Single source of truth for which fields make an invoice valid.
const validateImportInvoice = (invoice) => {
  const issues = [];
  if (!invoice.document) issues.push("No document number - please set one");
  if (!invoice.name) issues.push("No vendor name - please set one");
  if (!invoice.cif) issues.push("No CIF - please set one");
  if (!invoice.issue_date) issues.push("No issue date - please set one");
  if (!invoice.phase?.phase_id) issues.push("No phase assigned - please select one");
  if (!invoice.category?.category_id) issues.push("No category assigned - please select one");

  const wasReady = invoice.ready_to_save;
  invoice.issues = issues;
  invoice.has_issues = issues.length > 0;
  invoice.ready_to_save = issues.length === 0;
  // Auto-tick a row once its issues are resolved; a row with issues is never selected
  invoice.selected = invoice.ready_to_save && (invoice.selected || !wasReady);
};

const recomputeStats = (state) => {
  const invoices = state.importPreview.invoices;
  const stats = state.importPreview.stats;
  stats.total = invoices.length;
  stats.ready_to_save = invoices.filter((inv) => inv.ready_to_save).length;
  stats.validation_issues = invoices.filter((inv) => inv.has_issues).length;
  stats.new_count = invoices.filter((inv) => inv.import_action !== "update").length;
  stats.modified_count = invoices.filter((inv) => inv.import_action === "update").length;
  stats.selected_ready = invoices.filter((inv) => inv.selected && inv.ready_to_save).length;
};

const initialState = {
  importPreview: {
    invoices: [],
    stats: {
      total: 0,
      ready_to_save: 0,
      validation_issues: 0,
      parsing_errors: [],
      new_count: 0,
      modified_count: 0,
      unchanged_count: 0,
      selected_ready: 0,
    },
  },
};

const vendorInvoiceSlice = createSlice({
  name: "vendorInvoices",
  initialState,
  reducers: {
    setImportPreview: (state, action) => {
      // Every row without issues (new or modified) starts ticked
      state.importPreview.invoices = action.payload.data.map((inv) => ({
        ...inv,
        selected: !!inv.ready_to_save,
      }));
      state.importPreview.stats = {
        parsing_errors: action.payload.parsing_errors || [],
        unchanged_count: action.payload.unchanged_count || 0,
      };
      recomputeStats(state);
    },

    removeImportInvoice: (state, action) => {
      state.importPreview.invoices = state.importPreview.invoices.filter((_, i) => i !== action.payload);
      recomputeStats(state);
    },

    toggleImportInvoiceSelected: (state, action) => {
      const invoice = state.importPreview.invoices[action.payload];
      if (!invoice || !invoice.ready_to_save) return;
      invoice.selected = !invoice.selected;
      recomputeStats(state);
    },

    setAllImportInvoicesSelected: (state, action) => {
      state.importPreview.invoices.forEach((inv) => { inv.selected = action.payload && inv.ready_to_save; });
      recomputeStats(state);
    },

    updateImportInvoiceField: (state, action) => {
      const { index, field, value } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;

      invoice[field] = value;

      const newIssues = [];
      if (!invoice.document) newIssues.push("No document number - please set one");
      if (!invoice.name) newIssues.push("No vendor name - please set one");
      if (!invoice.cif) newIssues.push("No CIF - please set one");
      if (!invoice.issue_date) newIssues.push("No issue date - please set one");
      if (!invoice.phase?.phase_id) newIssues.push("No phase assigned - please select one");
      if (!invoice.category?.category_id) newIssues.push("No category assigned - please select one");

      invoice.issues = newIssues;
      invoice.has_issues = newIssues.length > 0;
      invoice.ready_to_save = newIssues.length === 0;

      const invoices = state.importPreview.invoices;
      state.importPreview.stats.ready_to_save = invoices.filter((inv) => inv.ready_to_save).length;
      state.importPreview.stats.validation_issues = invoices.filter((inv) => inv.has_issues).length;
    },

    updateImportInvoiceCategory: (state, action) => {
      const { index, categoryId, categoryName } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;

      invoice.category = { category_id: categoryId, category_name: categoryName };

      const newIssues = invoice.issues.filter((issue) => !issue.toLowerCase().includes("category"));
      if (!categoryId) newIssues.push("No category assigned - please select one");

      invoice.issues = newIssues;
      invoice.has_issues = newIssues.length > 0;
      invoice.ready_to_save = newIssues.length === 0;

      const invoices = state.importPreview.invoices;
      state.importPreview.stats.ready_to_save = invoices.filter((inv) => inv.ready_to_save).length;
      state.importPreview.stats.validation_issues = invoices.filter((inv) => inv.has_issues).length;
    },

    updateImportInvoicePhase: (state, action) => {
      const { index, phaseId, phaseName } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;

      invoice.phase = { phase_id: phaseId, phase_name: phaseName };
      // A block belongs to a phase, so clear it whenever the phase changes
      invoice.block = { block_id: null, block_name: null };

      const newIssues = invoice.issues.filter((issue) => !issue.toLowerCase().includes("phase"));
      if (!phaseId) newIssues.push("No phase assigned - please select one");

      invoice.issues = newIssues;
      invoice.has_issues = newIssues.length > 0;
      invoice.ready_to_save = newIssues.length === 0;

      const invoices = state.importPreview.invoices;
      state.importPreview.stats.ready_to_save = invoices.filter((inv) => inv.ready_to_save).length;
      state.importPreview.stats.validation_issues = invoices.filter((inv) => inv.has_issues).length;
    },

    updateImportInvoiceSubCategory: (state, action) => {
      const { index, subCategoryId, subCategoryName } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;
      invoice.sub_category = { sub_category_id: subCategoryId, sub_category_name: subCategoryName };
    },

    updateImportInvoiceBlock: (state, action) => {
      const { index, blockId, blockName } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;
      invoice.block = { block_id: blockId, block_name: blockName };
    },

    applyImportInvoiceEdit: (state, action) => {
      const { index, data, applyToVendor } = action.payload;
      const invoice = state.importPreview.invoices[index];
      if (!invoice) return;

      Object.assign(invoice, data);
      validateImportInvoice(invoice);

      // Optionally push the resolved mappings onto every invoice from the same vendor
      if (applyToVendor && invoice.cif) {
        const mapping = {
          phase: data.phase,
          category: data.category,
          sub_category: data.sub_category,
          block: data.block,
        };
        state.importPreview.invoices.forEach((inv, i) => {
          if (i !== index && inv.cif === invoice.cif) {
            Object.assign(inv, mapping);
            validateImportInvoice(inv);
          }
        });
      }

      recomputeStats(state);
    },

    clearImportPreview: (state) => {
      state.importPreview = initialState.importPreview;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postVendorsInvoices.fulfilled, (state) => {
      state.importPreview = initialState.importPreview;
    });
  },
});

export const {
  setImportPreview,
  removeImportInvoice,
  toggleImportInvoiceSelected,
  setAllImportInvoicesSelected,
  updateImportInvoiceField,
  updateImportInvoiceCategory,
  updateImportInvoicePhase,
  updateImportInvoiceSubCategory,
  updateImportInvoiceBlock,
  applyImportInvoiceEdit,
  clearImportPreview,
} = vendorInvoiceSlice.actions;

export default vendorInvoiceSlice.reducer;
